import { useMemo, useState, type ChangeEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { broadcast, type BroadcastAttachment, type BroadcastRecipient } from "@/lib/api";
import { useCurrentEvent } from "@/lib/event-context";

type CsvStats = {
  rows: number;
  imported: number;
  invalid: number;
  duplicates: number;
};

type CsvState = {
  fileName: string;
  recipients: BroadcastRecipient[];
  stats: CsvStats;
  error: string | null;
};

type AttachmentState = BroadcastAttachment & { size: number };

const EMPTY_CSV: CsvState = {
  fileName: "",
  recipients: [],
  stats: { rows: 0, imported: 0, invalid: 0, duplicates: 0 },
  error: null,
};

const MAX_ATTACHMENT_COUNT = 5;
const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;

function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let value = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === "\"") {
      if (inQuotes && text[i + 1] === "\"") {
        value += "\"";
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      row.push(value);
      value = "";
    } else if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(value);
      if (row.some((cell) => cell.trim())) rows.push(row);
      row = [];
      value = "";
    } else {
      value += ch;
    }
  }

  row.push(value);
  if (row.some((cell) => cell.trim())) rows.push(row);
  return rows;
}

function normalizeHeader(value: string): string {
  return value.replace(/^\uFEFF/, "").trim().toLowerCase();
}

function findColumn(headers: string[], candidates: string[]): number {
  const normalized = headers.map(normalizeHeader);
  return candidates
    .map((candidate) => normalized.indexOf(normalizeHeader(candidate)))
    .find((idx) => idx >= 0) ?? -1;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = () => reject(new Error(`${file.name} kon niet worden gelezen`));
    reader.readAsDataURL(file);
  });
}

function attachmentPayloads(attachments: AttachmentState[]): BroadcastAttachment[] {
  return attachments.map(({ filename, content, content_type }) => ({ filename, content, content_type }));
}

function parseContactsCsv(text: string, fileName: string): CsvState {
  const rows = parseCsvRows(text);
  if (rows.length < 2) {
    return { ...EMPTY_CSV, fileName, error: "CSV bevat geen contactregels." };
  }

  const headers = rows[0];
  const emailIdx = findColumn(headers, ["E-mail 1", "E-mailadres", "E-mail", "Email", "email"]);
  const firstIdx = findColumn(headers, ["Voornaam", "First name", "First Name"]);
  const lastIdx = findColumn(headers, ["Achternaam", "Last name", "Last Name"]);
  const companyIdx = findColumn(headers, ["Bedrijf", "Company", "Organisatie"]);
  const statusIdx = findColumn(headers, ["Status e-mailabonnee", "Status emailabonnee", "Subscription status"]);
  const sourceIdx = findColumn(headers, ["Bron", "Source"]);

  if (emailIdx < 0) {
    return { ...EMPTY_CSV, fileName, error: "CSV mist een e-mailkolom. Verwacht bijvoorbeeld 'E-mail 1'." };
  }

  const seen = new Set<string>();
  const recipients: BroadcastRecipient[] = [];
  const stats: CsvStats = { rows: rows.length - 1, imported: 0, invalid: 0, duplicates: 0 };

  const cell = (row: string[], idx: number) => (idx >= 0 ? (row[idx] ?? "").trim() : "");

  for (const row of rows.slice(1)) {
    const status = cell(row, statusIdx);
    const email = cell(row, emailIdx).toLowerCase();
    if (!isValidEmail(email)) {
      stats.invalid++;
      continue;
    }
    if (seen.has(email)) {
      stats.duplicates++;
      continue;
    }

    seen.add(email);
    const firstName = cell(row, firstIdx);
    const lastName = cell(row, lastIdx);
    const name = [firstName, lastName].filter(Boolean).join(" ");
    recipients.push({
      email,
      first_name: firstName,
      last_name: lastName,
      name,
      company: cell(row, companyIdx),
      status,
      source: cell(row, sourceIdx) || fileName,
    });
  }

  stats.imported = recipients.length;
  return { fileName, recipients, stats, error: null };
}

function defaultSelectedEmails(recipients: BroadcastRecipient[]): Set<string> {
  const hasSubscriptionStatus = recipients.some((r) => r.status?.trim());
  const selected = hasSubscriptionStatus
    ? recipients.filter((r) => r.status?.trim().toLowerCase() === "ingeschreven")
    : recipients;
  return new Set(selected.map((r) => r.email));
}

export function MailingsIndex() {
  const { data: event } = useCurrentEvent();

  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [csv, setCsv] = useState<CsvState>(EMPTY_CSV);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [recipientQuery, setRecipientQuery] = useState("");
  const [attachments, setAttachments] = useState<AttachmentState[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  const selectedRecipients = useMemo(
    () => csv.recipients.filter((recipient) => selectedEmails.has(recipient.email)),
    [csv.recipients, selectedEmails],
  );
  const visibleRecipients = useMemo(() => {
    const query = recipientQuery.trim().toLowerCase();
    if (!query) return csv.recipients;
    return csv.recipients.filter((recipient) => [
      recipient.name,
      recipient.first_name,
      recipient.last_name,
      recipient.company,
      recipient.email,
      recipient.status,
    ].some((value) => value?.toLowerCase().includes(query)));
  }, [csv.recipients, recipientQuery]);
  const visibleSelectedCount = visibleRecipients.filter((recipient) => selectedEmails.has(recipient.email)).length;
  const allVisibleSelected = visibleRecipients.length > 0 && visibleSelectedCount === visibleRecipients.length;
  const readyLabel = `${selectedRecipients.length} geselecteerd`;
  const previewHtml = useMemo(() => html || "<p></p>", [html]);

  const buildPayload = (dryRun: boolean) => ({
    subject: subject.trim(),
    html: html.trim(),
    segment: { event_id: event?.id },
    recipients: selectedRecipients,
    attachments: attachmentPayloads(attachments),
    source: csv.fileName ? `csv:${csv.fileName}` : "csv",
    campaign_name: subject.trim() || undefined,
    dry_run: dryRun,
  });

  const sendMut = useMutation({
    mutationFn: () => broadcast(buildPayload(false)),
    onSuccess: (r) => setToast(`Verzonden: ${r.sent} OK, ${r.failed} mislukt (${r.recipient_count} totaal)`),
    onError: (e: Error) => setToast(`Fout: ${e.message}`),
  });

  const canSubmit = selectedRecipients.length > 0 && subject.trim().length > 0 && html.trim().length > 0;

  function handleCsvText(nextText: string, fileName: string) {
    const parsed = parseContactsCsv(nextText, fileName);
    setCsv(parsed);
    setSelectedEmails(defaultSelectedEmails(parsed.recipients));
    setRecipientQuery("");
  }

  function handleCsvFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => handleCsvText(String(reader.result ?? ""), file.name);
    reader.onerror = () => setCsv({ ...EMPTY_CSV, fileName: file.name, error: "CSV kon niet worden gelezen." });
    reader.readAsText(file, "utf-8");
  }

  function setRecipientsSelected(recipients: BroadcastRecipient[], selected: boolean) {
    setSelectedEmails((current) => {
      const next = new Set(current);
      for (const recipient of recipients) {
        if (selected) next.add(recipient.email);
        else next.delete(recipient.email);
      }
      return next;
    });
  }

  function toggleRecipient(email: string) {
    setSelectedEmails((current) => {
      const next = new Set(current);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  async function handleAttachmentFiles(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.currentTarget.value = "";
    if (files.length === 0) return;

    const nextRawBytes = attachments.reduce((sum, a) => sum + a.size, 0) + files.reduce((sum, f) => sum + f.size, 0);
    if (attachments.length + files.length > MAX_ATTACHMENT_COUNT) {
      setToast(`Maximaal ${MAX_ATTACHMENT_COUNT} bijlagen per broadcast`);
      return;
    }
    if (nextRawBytes > MAX_ATTACHMENT_BYTES) {
      setToast(`Bijlagen zijn samen te groot (max. ${formatBytes(MAX_ATTACHMENT_BYTES)})`);
      return;
    }

    try {
      const converted = await Promise.all(files.map(async (file) => ({
        filename: file.name,
        content: await readAsBase64(file),
        content_type: file.type || "application/octet-stream",
        size: file.size,
      })));
      setAttachments((current) => [...current, ...converted]);
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Bijlage kon niet worden gelezen");
    }
  }

  function removeAttachment(filename: string) {
    setAttachments((current) => current.filter((a) => a.filename !== filename));
  }

  return (
    <>
      <div className="broadcast-layout">
        <div className="panel">
          <div className="panel-h">
            <h3>Nieuwe broadcast</h3>
            <span className="broadcast-state">{readyLabel}</span>
          </div>

          <div className="broadcast-steps">
            <section className="broadcast-step">
              <div className="step-head">
                <span>1</span>
                <div>
                  <h4>Ontvangers</h4>
                  <p>Contactenbestand</p>
                </div>
              </div>

              <div className="csv-import">
                <label className={`upload-card ${csv.fileName ? "loaded" : ""}`}>
                  <input type="file" accept=".csv,text/csv" onChange={handleCsvFile} />
                  <span className="upload-icon">+</span>
                  <span>
                    <strong>{csv.fileName || "CSV-bestand kiezen"}</strong>
                    <small>{csv.fileName ? "Klik om een andere lijst te kiezen" : "Voornaam, Achternaam, E-mail 1 en Status e-mailabonnee worden herkend"}</small>
                  </span>
                </label>

                {csv.error ? (
                  <div className="note error">{csv.error}</div>
                ) : csv.stats.rows > 0 ? (
                  <>
                    <div className="metric-grid">
                      <div><strong>{csv.stats.imported}</strong><span>gevonden</span></div>
                      <div><strong>{selectedRecipients.length}</strong><span>geselecteerd</span></div>
                      <div><strong>{csv.stats.duplicates}</strong><span>dubbel</span></div>
                      <div><strong>{csv.stats.invalid}</strong><span>ongeldig</span></div>
                    </div>

                    <div className="recipient-picker">
                      <div className="recipient-picker-head">
                        <input
                          className="search"
                          value={recipientQuery}
                          onChange={(e) => setRecipientQuery(e.target.value)}
                          placeholder="Zoek naam, bedrijf of e-mail"
                        />
                        <div className="recipient-picker-actions">
                          <button
                            type="button"
                            onClick={() => setRecipientsSelected(visibleRecipients, !allVisibleSelected)}
                            disabled={visibleRecipients.length === 0}
                          >
                            {allVisibleSelected ? "Deselecteer zichtbaar" : "Selecteer zichtbaar"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setRecipientsSelected(csv.recipients, false)}
                            disabled={selectedRecipients.length === 0}
                          >
                            Wis selectie
                          </button>
                        </div>
                      </div>
                      <div className="recipient-list">
                        {visibleRecipients.map((recipient) => (
                          <label key={recipient.email} className={`recipient-choice ${selectedEmails.has(recipient.email) ? "selected" : ""}`}>
                            <input
                              type="checkbox"
                              checked={selectedEmails.has(recipient.email)}
                              onChange={() => toggleRecipient(recipient.email)}
                            />
                            <span>
                              <strong>{recipient.name || [recipient.first_name, recipient.last_name].filter(Boolean).join(" ") || recipient.company || recipient.email}</strong>
                              <small>{recipient.email}{recipient.company ? ` · ${recipient.company}` : ""}{recipient.status ? ` · ${recipient.status}` : ""}</small>
                            </span>
                          </label>
                        ))}
                        {visibleRecipients.length === 0 && <div className="note">Geen ontvangers gevonden.</div>}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="note">Nog geen lijst gekozen.</div>
                )}
              </div>
            </section>

            <section className="broadcast-step">
              <div className="step-head">
                <span>2</span>
                <div>
                  <h4>Bericht</h4>
                  <p>Onderwerp en HTML</p>
                </div>
              </div>

              <div className="field">
                <label>Onderwerp</label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="bv. Uitnodiging Ode aan de Haring 2026" />
              </div>

              <div className="compose-grid">
                <div className="field">
                  <label>HTML</label>
                  <textarea
                    className="html-compose"
                    value={html}
                    onChange={(e) => setHtml(e.target.value)}
                    rows={16}
                    spellCheck={false}
                    placeholder={"<p>Beste {{voornaam}},</p>\n<p>...</p>\n<p><a href=\"{{unsubscribe_link}}\">Uitschrijven</a></p>"}
                  />
                </div>
                <div className="field">
                  <label>Preview</label>
                  <iframe className="html-preview" srcDoc={previewHtml} title="Broadcast preview" sandbox="" />
                </div>
              </div>

              <div className="attachment-box">
                <div className="attachment-head">
                  <span>Bijlagen</span>
                  <small>{attachments.length}/{MAX_ATTACHMENT_COUNT} · max {formatBytes(MAX_ATTACHMENT_BYTES)}</small>
                </div>
                <label className="attachment-add">
                  <input type="file" multiple onChange={handleAttachmentFiles} />
                  Bestand toevoegen
                </label>
                {attachments.length > 0 && (
                  <div className="attachment-list">
                    {attachments.map((attachment) => (
                      <div key={attachment.filename} className="attachment-row">
                        <span>
                          <strong>{attachment.filename}</strong>
                          <small>{formatBytes(attachment.size)}</small>
                        </span>
                        <button type="button" onClick={() => removeAttachment(attachment.filename)}>Verwijder</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>

        <aside className="broadcast-side">
          <div className="panel">
            <div className="panel-h"><h3>Controle</h3></div>
            <div className="send-check">
              <div>
                <span>Geselecteerd</span>
                <strong>{selectedRecipients.length} van {csv.recipients.length}</strong>
              </div>
              <div>
                <span>Onderwerp</span>
                <strong>{subject || "-"}</strong>
              </div>
              <div>
                <span>Bijlagen</span>
                <strong>{attachments.length ? `${attachments.length} bestand(en)` : "-"}</strong>
              </div>
            </div>
            <div className="send-actions">
              <button
                className="btn-admin sm"
                disabled={!canSubmit || sendMut.isPending}
                onClick={() => {
                  const count = selectedRecipients.length;
                  if (confirm(`Echt verzenden naar ${count} ontvangers?`)) sendMut.mutate();
                }}
              >
                {sendMut.isPending ? "Verzenden..." : "Verstuur broadcast"}
              </button>
            </div>
          </div>
        </aside>
      </div>

      {toast && <div className="toast" onClick={() => setToast(null)}>{toast}</div>}
    </>
  );
}
